import type { UUID } from '@cmg/common';
import type { DataGridProps } from '@cmg/data-grid';
import { DataGridContextProvider, ServerDataGrid, useDataGridContext } from '@cmg/data-grid';
import { useMemo } from 'react';

import { useRefetchOnReactiveVar } from '../../../../../../../common/hooks/useRefetchOnReactiveVar/useRefetchOnReactiveVar';
import type { RolodexRecordStatus } from '../../../../../../../graphql';
import { useUpdateRelationship } from '../../../../components/shared-columns/hooks/useUpdateRelationship';
import { firmsSalesCoverageGridColumns } from './columns';
import type { FirmSalesCoverageGridContext } from './FirmsSalesCoverageGrid.model';
import {
  canInvestorFirmEditCoverageRelationshipStatus,
  INITIAL_INVESTOR_SALES_COVERAGE_GRID_STATE,
  refetchFirmsSalesCoverageGridReactiveVar,
} from './FirmsSalesCoverageGrid.model';
import type { FirmSalesCoverageGridItemFragment } from './graphql/__generated__';
import { useCellValueChanged } from './hooks/useCellValueChanged';
import { useFirmsSalesCoverageGridDataSource } from './hooks/useFirmsSalesCoverageGridDataSource';

const AUTO_SIZE_STRATEGY: DataGridProps<FirmSalesCoverageGridItemFragment>['autoSizeStrategy'] = {
  type: 'fitGridWidth',
};

export type FirmsSalesCoverageGridProps = Readonly<{
  tenantId?: UUID;
  firmId: string;
  firmStatus: RolodexRecordStatus;
}>;

function FirmsSalesCoverageGridComponent({
  tenantId,
  firmId,
  firmStatus,
}: FirmsSalesCoverageGridProps) {
  const dataSource = useFirmsSalesCoverageGridDataSource({ firmId, tenantId });
  const { refreshServerSide } = useDataGridContext();
  const handleUpdateRelationship = useUpdateRelationship({
    tenantId,
    onComplete: refreshServerSide,
  });

  const handleCellValueChanged = useCellValueChanged({
    onUpdateRelationship: handleUpdateRelationship,
  });

  useRefetchOnReactiveVar({
    onRefetch: refreshServerSide,
    reactiveVar: refetchFirmsSalesCoverageGridReactiveVar,
  });
  const context = useMemo<FirmSalesCoverageGridContext>(() => {
    return {
      isRelationshipStatusEditable: canInvestorFirmEditCoverageRelationshipStatus(firmStatus),
    };
  }, [firmStatus]);

  return (
    <ServerDataGrid<FirmSalesCoverageGridItemFragment, FirmSalesCoverageGridContext>
      serverSideDatasource={dataSource}
      columnDefs={firmsSalesCoverageGridColumns}
      autoSizeStrategy={AUTO_SIZE_STRATEGY}
      onCellValueChanged={handleCellValueChanged}
      context={context}
      initialState={INITIAL_INVESTOR_SALES_COVERAGE_GRID_STATE}
    />
  );
}

export function FirmsSalesCoverageGrid(props: FirmsSalesCoverageGridProps) {
  return (
    <DataGridContextProvider>
      <FirmsSalesCoverageGridComponent {...props} />
    </DataGridContextProvider>
  );
}
